import React from "react";
import "./CompanyAndSharesResult.css";
import { Text, View } from "@react-pdf/renderer";
import { styles } from "../styles/Slide6Styles";
import {
  CallOption,
  CallOptions,
  CliffPeriod,
  CountryType,
  FoundersType,
  TimeType,
  VestingFrequency,
} from "../ResultContext";
import ResultsNavigationBar from "../components/ResultsNavigationBar";
import { BackLoaded } from "../constants/BackLoaded";

function StringBuilder(callOptions: CallOptions) {
  let resultString = "";

  if (!callOptions.callOptions) {
    return "No call options";
  }

  if (callOptions.allShares) {
    resultString += `${callOptions.shareValue} `;
  } else if (callOptions.shareValue === CallOption.FairMarketDiscount) {
    resultString += `${callOptions.percentageOfShares}% at fair market value `;
  } else {
    resultString += `${callOptions.percentageOfShares}% ${
      callOptions.shareValue === CallOption.Euro ||
      callOptions.shareValue === CallOption.InitialSubscription
        ? "for"
        : "at"
    } ${callOptions.shareValue.toLowerCase()} `;
  }

  if (callOptions.shareValue === CallOption.CustomPrice) {
    resultString += `(€${callOptions.shareValuePrice})`;
  }

  if (callOptions.shareValue === CallOption.FairMarketDiscount) {
    resultString += `with a discount of ${callOptions.shareValueDiscount}% `;
  }

  return resultString;
}

function BackLoadedString(vestingDurationResult: {
  time: number;
  type: TimeType;
}) {
  const backLoadedList = BackLoaded[vestingDurationResult.time];
  const resultString = "[" + Object.values(backLoadedList).join("%-") + "%]";

  return resultString;
}

export function Slide6({
  vestingFrequencyResult,
  vestingDurationResult,
  vestingCliffResult,
  vestingStartResult,
  foundersResult,
  callOptionsGoodResult,
  callOptionsEarlyResult,
  callOptionsBadResult,
  badAfterVestingResult,
  vestingSharesResult,
}: {
  vestingFrequencyResult: {
    frequency: VestingFrequency;
    type: string;
  };
  vestingDurationResult: {
    time: number;
    type: TimeType;
  };
  vestingCliffResult: CliffPeriod;
  vestingStartResult: string[];
  foundersResult: {
    name: string;
    country: CountryType;
    type: FoundersType;
  }[];
  callOptionsGoodResult: {
    vested: CallOptions;
    unvested: CallOptions;
    vestedBad: CallOptions;
  };
  callOptionsEarlyResult: {
    vested: CallOptions;
    unvested: CallOptions;
    vestedBad: CallOptions;
  };
  callOptionsBadResult: {
    vested: CallOptions;
    unvested: CallOptions;
    vestedBad: CallOptions;
  };
  badAfterVestingResult: boolean;
  vestingSharesResult: string[];
}) {
  if (
    !vestingFrequencyResult ||
    !vestingCliffResult ||
    !vestingDurationResult ||
    !foundersResult ||
    !vestingStartResult ||
    !callOptionsGoodResult ||
    !callOptionsEarlyResult ||
    !callOptionsBadResult ||
    !vestingSharesResult
  ) {
    return <Text>Not all information is filled in for this section</Text>;
  }
  return (
    <View style={styles.backgroundBlue}>
      <ResultsNavigationBar selectIdx={4} />
      <View style={styles.callOptionContainer}>
        <View style={styles.callOptionBox}>
          <View style={styles.titleBox}>
            {vestingSharesResult.every((val) => val === "all") ? (
              <Text style={styles.textTitle}>
                The Founders, grant to each other, a (pro rata) CALL OPTION on
                their resp. shares in the Company, subject to:
              </Text>
            ) : (
              <Text style={styles.textTitle}>
                The Founders, grant to each other, a (pro rata) CALL OPTION on
                their resp. shares * in the Company, subject to:
              </Text>
            )}
          </View>
          <View style={styles.infoBox}>
            <View style={styles.infoBoxLeft}>
              <View style={styles.infoBoxLeftTextUpperBox}>
                <Text style={styles.textInfoBox}>
                  - a {vestingFrequencyResult.type.toLowerCase()}{" "}
                  {vestingFrequencyResult.type === "Linear"
                    ? null
                    : ` - ${BackLoadedString(vestingDurationResult)} - `}
                  vesting schedule of {vestingDurationResult.time} year(s)
                </Text>
              </View>
              <View style={styles.infoBoxLeftTextLowerBox}>
                <Text style={styles.textInfoBox}>
                  - with {vestingFrequencyResult.frequency.toLowerCase()}{" "}
                  vesting
                </Text>
                <Text style={styles.textInfoBox}>
                  {vestingCliffResult.cliffOptions
                    ? `- with a ${vestingCliffResult.cliffShares} months cliff `
                    : null}
                </Text>
              </View>
            </View>
            <View style={styles.infoBoxRight}>
              {vestingStartResult.map((item, index) => {
                return (
                  <Text style={styles.textInfoBox} key={index}>
                    as of {item} for {foundersResult[index].name}
                  </Text>
                );
              })}
            </View>
          </View>
          <View style={styles.leaverEventsBox}>
            <View style={styles.leaverEventsBoxLeft}>
              <View style={styles.leaverEventsBoxSubTitle}>
                <Text style={styles.textLeaverTitle}>
                  The following Leaver Events:
                </Text>
              </View>
              <View style={styles.leaverEventsBoxColumns}>
                <View style={styles.leaverEventsBoxLeftLeft}>
                  <Text style={styles.textLeaverEventsBoxLeftLeft}>
                    Good Leaver:
                  </Text>
                  <Text style={styles.textLeaverEventsBoxLeftLeft}>
                    Early Leaver:
                  </Text>
                  <Text style={styles.textLeaverEventsBoxLeftLeft}>
                    Bad Leaver:
                  </Text>
                </View>
                <View style={styles.leaverEventsBoxLeftRight}>
                  <View style={styles.leaverEventsBoxTextPairLeft}>
                    <Text style={styles.textLeaverEventsBoxLeftRight}>
                      Vested Shares
                    </Text>
                    <Text style={styles.textLeaverEventsBoxLeftRight}>
                      Unvested Shares
                    </Text>
                  </View>
                  <View style={styles.leaverEventsBoxTextPairLeft}>
                    <Text style={styles.textLeaverEventsBoxLeftRight}>
                      Vested Shares
                    </Text>
                    <Text style={styles.textLeaverEventsBoxLeftRight}>
                      Unvested Shares
                    </Text>
                  </View>
                  <View style={styles.leaverEventsBoxTextPairLeft}>
                    <Text style={styles.textLeaverEventsBoxLeftRight}>
                      Vested Shares
                    </Text>
                    <Text style={styles.textLeaverEventsBoxLeftRight}>
                      Unvested Shares
                    </Text>
                  </View>
                </View>
              </View>
            </View>
            <View style={styles.leaverEventsBoxRight}>
              <View style={styles.leaverEventsBoxSubTitle}>
                <Text style={styles.textLeaverTitle}>
                  Call option prices for Vested and Unvested Shares:
                </Text>
              </View>
              <View style={styles.leaverEventsBoxRightContent}>
                <View style={styles.leaverEventsBoxTextPairRight}>
                  <Text style={styles.textLeaverEventsBoxRightContent}>
                    - {StringBuilder(callOptionsGoodResult.vested)}
                  </Text>
                  <Text style={styles.textLeaverEventsBoxRightContent}>
                    - {StringBuilder(callOptionsGoodResult.unvested)}
                  </Text>
                </View>
                <View style={styles.marginTop11} />
                <View style={styles.leaverEventsBoxTextPairRight}>
                  <Text style={styles.textLeaverEventsBoxRightContent}>
                    - {StringBuilder(callOptionsEarlyResult.vested)}
                  </Text>
                  <Text style={styles.textLeaverEventsBoxRightContent}>
                    - {StringBuilder(callOptionsEarlyResult.unvested)}
                  </Text>
                </View>
                <View style={styles.marginTop11} />
                <View style={styles.leaverEventsBoxTextPairRight}>
                  <Text style={styles.textLeaverEventsBoxRightContent}>
                    - {StringBuilder(callOptionsBadResult.vested)}
                  </Text>
                  <Text style={styles.textLeaverEventsBoxRightContent}>
                    - {StringBuilder(callOptionsBadResult.unvested)}
                  </Text>
                </View>
              </View>
            </View>
          </View>
          <View style={styles.afterVestingInfoBox}>
            {vestingSharesResult.every((val) => val === "all") ? (
              <Text style={styles.textAfterVestingInfo}>
                After the vesting period, The Founders, grant to each other, a
                CALL OPTION on their resp. shares in the Company, subject to:
              </Text>
            ) : (
              <Text style={styles.textAfterVestingInfo}>
                After the vesting period, The Founders, grant to each other, a
                CALL OPTION on their resp. shares* in the Company, subject to:
              </Text>
            )}
          </View>
          <View style={styles.afterVestingContentBox}>
            <View style={styles.afterVestingContentBoxLeft}>
              <View style={styles.afterVestingContentBoxLeftLeft}>
                <Text style={styles.textAfterVestingContentBoxLeftLeft}>
                  Bad Leaver:
                </Text>
              </View>
              <View style={styles.afterVestingContentBoxLeftRight}>
                <Text style={styles.textAfterVestingContentBoxLeftRight}>
                  Vested Shares
                </Text>
              </View>
            </View>
            <View style={styles.afterVestingContentBoxRight}>
              {badAfterVestingResult ? (
                <Text style={styles.textLeaverEventsBoxRightContent}>
                  -
                  {callOptionsBadResult.vestedBad.callOptions
                    ? ` Call option on ${
                        callOptionsBadResult.vestedBad.allShares
                          ? "all shares"
                          : `${callOptionsBadResult.vestedBad.percentageOfShares}% of the shares`
                      } at ${callOptionsBadResult.vestedBad.shareValue} ${
                        callOptionsBadResult.vestedBad.shareValue ===
                          CallOption.CustomPrice ||
                        callOptionsBadResult.vestedBad.shareValue ===
                          CallOption.FairMarketDiscount
                          ? `(${
                              callOptionsBadResult.vestedBad.shareValue ===
                              CallOption.FairMarketDiscount
                                ? `${callOptionsBadResult.vestedBad.shareValueDiscount}% discount`
                                : `€ ${callOptionsBadResult.vestedBad.shareValuePrice}`
                            })`
                          : ""
                      }`
                    : " No call options"}
                </Text>
              ) : (
                <Text style={styles.textLeaverEventsBoxRightContent}>/</Text>
              )}
            </View>
          </View>
        </View>
        {vestingSharesResult.every((val) => val === "all") ? null : (
          <Text style={styles.textSideNote}>
            *The shares subject to the vesting schedule and the call options
            (cfr. see above).
          </Text>
        )}
      </View>
    </View>
  );
}
